import {Component, Input, OnInit, inject, input, signal, effect, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ProjectAnalysisDefinitionService, ToastService} from '../../services';
import {ProjectDetails, QuickscanDefinition} from '../../models';
import {Category} from '../../models';
import {ProjectStatus, ProjectSteps} from '../../enums';
import {firstValueFrom} from 'rxjs';
import {AccessibleStepsService} from '../../services/accessible-steps/accessible-steps.service';

export interface IProjectNavSection {
    stepId: ProjectSteps;
    label: string;
}

/**
 * PRM 2.0 left project navigation, replaces the progress bar dots.
 * Steps after the quickscan unlock once the quickscan has been made
 * definitive; the active step shows its categories as sub items.
 */
@Component({
    selector: 'app-project-nav-menu',
    templateUrl: './project-nav-menu.component.html',
    styleUrls: ['./project-nav-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectNavMenuComponent implements OnInit {
    private static readonly MENU_STEPS: ProjectSteps[] = [
        ProjectSteps.DETAILS,
        ProjectSteps.QUICKSCAN,
        ProjectSteps.MANDATING
    ];

    private static readonly RISKS_AND_OPPORTUNITIES_STEPS: ProjectSteps[] = [
        ProjectSteps.OPPORTUNITIES,
        ProjectSteps.RISKS,
        ProjectSteps.DOSSIER
    ];

    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private translateService = inject(TranslateService);
    private projectAnalysisDefinitionService = inject(ProjectAnalysisDefinitionService);
    private toastService = inject(ToastService);
    private accessibleStepsService = inject(AccessibleStepsService);

    public projectDetails = input<ProjectDetails | null>(null);
    @Input() public stepId: ProjectSteps;

    public sections: IProjectNavSection[] = [];
    public risksAndOpportunitiesSections: IProjectNavSection[] = [];
    public risksAndOpportunitiesLabel = '';
    public categories: Category[] = [];
    public projectSteps = ProjectSteps;
    public isOpen = signal(false);


    constructor() {
        effect(() => {
            if (this.projectDetails()) {
                this.sections = ProjectNavMenuComponent.MENU_STEPS.map((stepId) => ({
                    stepId,
                    label: this.getStepLabel(stepId)
                }));
                this.risksAndOpportunitiesSections = this.risksAndOpportunitiesSteps().map((stepId) => ({
                    stepId,
                    label: this.getStepLabel(stepId)
                }));
                this.risksAndOpportunitiesLabel = this.translateService.instant('STEPS.risks_and_opportunities_group');
            }
        });
    }

    async ngOnInit() {
        if (this.projectDetails().projectType === null) {
            this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_ANALYSIS_QUESTIONS.no_project_type'));
        } else {
            const definition = await firstValueFrom(
                this.projectAnalysisDefinitionService.getProjectDefinition(this.projectDetails().id)
            );
            this.categories = QuickscanDefinition.convertAnalysisDefinition(definition).categories;
        }
    }

    /**
     * A category with no quickscan questions is only hidden during the
     * Quickscan phase; once finalised, all its (TIS) questions show again.
     * Mirrors ProjectAnalysisQuestionsComponent.categoryHasVisibleQuestions().
     */
    public visibleCategories(): Category[] {
        const isQuickScanPhase = !this.projectDetails()?.isQuickscanFinalised();

        return this.categories.filter((category) => category.questions.some(
            (question) => !isQuickScanPhase || !!question.isQuickScan
        ));
    }

    public isActive(stepId: ProjectSteps): boolean {
        return this.stepId === stepId;
    }

    public toggle(): void {
        this.isOpen.update((open) => !open);
    }

    public close(): void {
        this.isOpen.set(false);
    }

    /**
     * Everything after the quickscan is locked until the quickscan
     * has been made definitive.
     */
    public isLocked(stepId: ProjectSteps): boolean {
        if (stepId === ProjectSteps.DETAILS || stepId === ProjectSteps.QUICKSCAN) {
            return false;
        }

        return this.projectDetails().status === ProjectStatus.INITIAL;
    }

    public openStep(stepId: ProjectSteps): void {
        this.close();

        if (this.isLocked(stepId) || this.isActive(stepId)) {
            return;
        }

        this.router.navigate(this.stepUrl(stepId));
    }

    public isRisksAndOpportunitiesActive(): boolean {
        return this.risksAndOpportunitiesSteps().some((stepId) => this.isActive(stepId));
    }

    private risksAndOpportunitiesSteps(): ProjectSteps[] {
        return ProjectNavMenuComponent.RISKS_AND_OPPORTUNITIES_STEPS.filter((stepId) =>
            this.accessibleStepsService.isStepAccessible(stepId)
        );
    }

    public isRisksAndOpportunitiesLocked(): boolean {
        return this.isLocked(ProjectSteps.RISKS);
    }

    public openRisksAndOpportunities(): void {
        this.close();

        const [firstStep] = this.risksAndOpportunitiesSteps();
        if (!firstStep || this.isRisksAndOpportunitiesLocked() || this.isRisksAndOpportunitiesActive()) {
            return;
        }

        this.router.navigate(this.stepUrl(firstStep));
    }

    // @deprecated Not used
    public openQuickscanCategory(category: Category): void {
        this.close();

        if (!this.isActive(ProjectSteps.QUICKSCAN)) {
            this.router.navigate(this.stepUrl(ProjectSteps.QUICKSCAN))
                .then(() => setTimeout(() => this.scrollToCategory(category), 300));
            return;
        }

        this.scrollToCategory(category);
    }

    public openAnalysisCategory(category: Category): void {
        this.close();
        this.router.navigate(this.stepUrl(ProjectSteps.QUICKSCAN), {queryParams: {category: category.id}});
        this.scrollToCategory(category);
    }

    public activeAnalysisCategoryId(): number {
        return Number(this.route.snapshot.queryParamMap.get('category'));
    }

    private stepUrl(stepId: ProjectSteps): string[] {
        return ['/projecten', `${this.projectDetails().id}`, 'step', `${stepId}`];
    }

    private scrollToCategory(category: Category): void {
        document.getElementById(`category_${category.id}`)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    private getStepLabel(step: ProjectSteps): string {
        let key = `STEPS.${step}`;
        if (ProjectSteps.QUICKSCAN === step && this.projectDetails()?.isQuickscanFinalised()) {
            key = `STEPS_V2.${ProjectSteps.TIS_QUESTIONS}`;
        }

        return this.translateService.instant(key, {tisLabelShort: this.projectDetails().projectInfo.tisLabelShort});
    }
}
